import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TablePagination,
  TextField,
  Typography,
} from '@mui/material';
import {
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
} from 'recharts';
import { useLocation } from 'react-router-dom';
import PageLayout from '../components/common/PageLayout';
import { get } from '../utils/api';

import { kotilingaIncludeCategoryParams, templeExcludeCategoryParams } from '../utils/kotilingaScope';

const PRESETS = [
  { k: 'last_12_months', t: 'Last 12 months' },
  { k: 'this_month', t: 'This month' },
  { k: 'last_3_months', t: 'Last 3 months' },
  { k: 'this_year', t: 'This year' },
  { k: 'last_year', t: 'Last year' },
  { k: 'all', t: 'All time' },
  { k: 'custom', t: 'Custom' },
];

function toYMD(d) {
  const yy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yy}-${mm}-${dd}`;
}

function formatCurrency(n) {
  const num = parseFloat(n);
  if (Number.isNaN(num)) return '$0.00';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 }).format(num);
}

function formatLabelByInterval(iso, interval) {
  if (!iso) return '';
  const d = new Date(iso);
  if (interval === 'year') return String(d.getUTCFullYear());
  if (interval === 'month') return d.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
  if (interval === 'week') return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' });
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export default function ScopedDashboardPage() {
  const location = useLocation();
  const path = location?.pathname || '';
  const scope = path.startsWith('/kotilinga') ? 'kotilinga' : 'temple';
  const scopeParams = scope === 'kotilinga'
    ? kotilingaIncludeCategoryParams()
    : templeExcludeCategoryParams();
  const scopeLabel = scope === 'kotilinga' ? 'Kotilinga' : 'Temple';

  const [rangePreset, setRangePreset] = useState('last_12_months');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [summary, setSummary] = useState(null);
  const [revenueSeries, setRevenueSeries] = useState([]);
  const [orderCountSeries, setOrderCountSeries] = useState([]);
  const [productSplit, setProductSplit] = useState([]);
  const [categorySplit, setCategorySplit] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [chartInterval, setChartInterval] = useState('month');
  const [donorSnapshots, setDonorSnapshots] = useState(null);
  const [productSnapshots, setProductSnapshots] = useState(null);
  const ROWS_PER_PAGE = 10;
  const [productPage, setProductPage] = useState(0);
  const [categoryPage, setCategoryPage] = useState(0);
  const [expandedProductId, setExpandedProductId] = useState(null);
  const [expandedCategoryId, setExpandedCategoryId] = useState(null);
  const [variationsByProductId, setVariationsByProductId] = useState({});
  const [productsByCategoryId, setProductsByCategoryId] = useState({});
  const [loadingVariations, setLoadingVariations] = useState(false);
  const [loadingCategoryProducts, setLoadingCategoryProducts] = useState(false);

  const applyPreset = useCallback((preset) => {
    if (preset === 'custom') return { startIso: '', endIso: '' };
    const now = new Date();
    const end = new Date(now);
    const start = new Date(now);
    let startYmd = '';
    let endYmd = toYMD(end);
    switch (preset) {
      case 'this_month':
        start.setDate(1);
        startYmd = toYMD(start);
        break;
      case 'last_3_months':
        start.setMonth(now.getMonth() - 2);
        startYmd = toYMD(start);
        break;
      case 'this_year':
        start.setMonth(0, 1);
        startYmd = toYMD(start);
        break;
      case 'last_year': {
        start.setFullYear(now.getFullYear() - 1);
        start.setMonth(0, 1);
        const endLastYear = new Date(now.getFullYear() - 1, 11, 31);
        startYmd = toYMD(start);
        endYmd = toYMD(endLastYear);
        break;
      }
      case 'all':
        startYmd = '';
        endYmd = '';
        break;
      default:
        // last_12_months
        start.setFullYear(now.getFullYear() - 1);
        startYmd = toYMD(start);
        break;
    }
    const startIso = startYmd ? new Date(start.getFullYear(), start.getMonth(), start.getDate()).toISOString() : '';
    let endIso = '';
    if (endYmd) {
      const endD = new Date(endYmd);
      endD.setHours(23, 59, 59, 999);
      endIso = endD.toISOString();
    }
    return { startIso, endIso };
  }, []);

  const loadData = useCallback(async (startIso, endIso) => {
    setLoading(true);
    setError('');
    try {
      const params = { ...scopeParams };
      if (startIso) params.start = startIso;
      if (endIso) params.end = endIso;

      let interval = 'month';
      if (!startIso && !endIso) interval = 'year';
      else if (startIso && endIso) {
        const startMs = new Date(startIso).getTime();
        const endMs = new Date(endIso).getTime();
        const days = (endMs - startMs) / (24 * 60 * 60 * 1000);
        if (days <= 31) interval = 'day';
        else if (days <= 95) interval = 'week';
        else if (days <= 400) interval = 'month';
        else interval = 'year';
      }
      setChartInterval(interval);

      const [sumRes, revRes, ordRes, prodRes, catRes] = await Promise.all([
        get('/ldb/analytics/summary', params),
        get('/ldb/analytics/revenue-series', { ...params, interval }),
        get('/ldb/analytics/order-count-series', { ...params, interval }),
        get('/ldb/analytics/product-split', { ...params, limit: 100 }),
        get('/ldb/analytics/category-split', { ...params, limit: 100 }),
      ]);
      setSummary(sumRes?.summary || null);
      setRevenueSeries(Array.isArray(revRes?.series) ? revRes.series : []);
      setOrderCountSeries(Array.isArray(ordRes?.series) ? ordRes.series : []);
      setProductSplit(Array.isArray(prodRes?.data) ? prodRes.data : []);
      setCategorySplit(Array.isArray(catRes?.data) ? catRes.data : []);
      setProductPage(0);
      setCategoryPage(0);
      setExpandedProductId(null);
      setExpandedCategoryId(null);
      setVariationsByProductId({});
      setProductsByCategoryId({});
    } catch (e) {
      setError(e?.message || `Failed to load ${scopeLabel.toLowerCase()} dashboard`);
      setSummary(null);
      setRevenueSeries([]);
      setOrderCountSeries([]);
      setProductSplit([]);
      setCategorySplit([]);
      setProductPage(0);
      setCategoryPage(0);
    } finally {
      setLoading(false);
    }
  }, [scope]);

  const currentDateRange = useMemo(() => {
    if (rangePreset === 'custom') {
      if (startDate && endDate) {
        const startIso = new Date(startDate).toISOString();
        const endD = new Date(endDate);
        endD.setHours(23, 59, 59, 999);
        return { startIso, endIso: endD.toISOString() };
      }
      return { startIso: '', endIso: '' };
    }
    if (rangePreset === 'all') return { startIso: '', endIso: '' };
    return applyPreset(rangePreset);
  }, [rangePreset, startDate, endDate, applyPreset]);

  const fetchProductVariations = useCallback(async (productId) => {
    if (variationsByProductId[productId] !== undefined) return;
    setLoadingVariations(true);
    try {
      const params = { ...scopeParams, product_id: productId, status: 'completed' };
      if (currentDateRange.startIso) params.start = currentDateRange.startIso;
      if (currentDateRange.endIso) params.end = currentDateRange.endIso;
      const res = await get('/ldb/stats/product-variations', params);
      const list = Array.isArray(res?.data) ? res.data : [];
      setVariationsByProductId((prev) => ({ ...prev, [productId]: list }));
    } catch (e) {
      setVariationsByProductId((prev) => ({ ...prev, [productId]: null }));
    } finally {
      setLoadingVariations(false);
    }
  }, [scopeParams, currentDateRange]);

  const fetchCategoryProducts = useCallback(async (categoryId) => {
    if (productsByCategoryId[categoryId] !== undefined) return;
    setLoadingCategoryProducts(true);
    try {
      const params = { ...scopeParams, category_id: categoryId };
      if (currentDateRange.startIso) params.start = currentDateRange.startIso;
      if (currentDateRange.endIso) params.end = currentDateRange.endIso;
      const res = await get('/ldb/analytics/products-by-category', params);
      const list = Array.isArray(res?.data) ? res.data : [];
      setProductsByCategoryId((prev) => ({ ...prev, [categoryId]: list }));
    } catch (e) {
      setProductsByCategoryId((prev) => ({ ...prev, [categoryId]: null }));
    } finally {
      setLoadingCategoryProducts(false);
    }
  }, [scopeParams, currentDateRange]);

  useEffect(() => {
    if (rangePreset === 'custom') {
      if (startDate && endDate) {
        const startIso = new Date(startDate).toISOString();
        const endD = new Date(endDate);
        endD.setHours(23, 59, 59, 999);
        loadData(startIso, endD.toISOString());
      } else {
        setSummary(null);
        setRevenueSeries([]);
        setOrderCountSeries([]);
        setProductSplit([]);
        setCategorySplit([]);
        setLoading(false);
      }
      return;
    }
    const { startIso, endIso } = applyPreset(rangePreset);
    if (rangePreset === 'all') {
      loadData('', '');
    } else {
      loadData(startIso, endIso);
    }
  }, [rangePreset, startDate, endDate, applyPreset, loadData]);

  useEffect(() => {
    let ignore = false;
    (async () => {
      try {
        const now = new Date();
        const endIso = now.toISOString();
        const thisYearStart = new Date(now.getFullYear(), 0, 1).toISOString();
        const last12StartDate = new Date(now);
        last12StartDate.setFullYear(last12StartDate.getFullYear() - 1);
        const last12Start = last12StartDate.toISOString();
        const last24StartDate = new Date(now);
        last24StartDate.setFullYear(last24StartDate.getFullYear() - 2);
        const last24Start = last24StartDate.toISOString();
        const last36StartDate = new Date(now);
        last36StartDate.setFullYear(last36StartDate.getFullYear() - 3);
        const last36Start = last36StartDate.toISOString();

        const baseParams = scope === 'kotilinga'
          ? { ...kotilingaIncludeCategoryParams(), status: 'completed' }
          : { ...templeExcludeCategoryParams(), status: 'completed' };

        const [overallRes, thisYearRes, last12Res, last24Res, last36Res] = await Promise.all([
          get('/ldb/stats/summary', baseParams),
          get('/ldb/stats/summary', { ...baseParams, start: thisYearStart, end: endIso }),
          get('/ldb/stats/summary', { ...baseParams, start: last12Start, end: endIso }),
          get('/ldb/stats/summary', { ...baseParams, start: last24Start, end: endIso }),
          get('/ldb/stats/summary', { ...baseParams, start: last36Start, end: endIso }),
        ]);

        if (!ignore) {
          setDonorSnapshots({
            overall: overallRes?.summary || null,
            thisYear: thisYearRes?.summary || null,
            last12: last12Res?.summary || null,
            last24: last24Res?.summary || null,
            last36: last36Res?.summary || null,
          });
        }
      } catch {
        if (!ignore) setDonorSnapshots(null);
      }
    })();
    return () => {
      ignore = true;
    };
  }, [scope]);

  useEffect(() => {
    let ignore = false;
    (async () => {
      try {
        const now = new Date();
        const endIso = now.toISOString();
        const thisYearStart = new Date(now.getFullYear(), 0, 1).toISOString();
        const last12StartDate = new Date(now);
        last12StartDate.setFullYear(last12StartDate.getFullYear() - 1);
        const last12Start = last12StartDate.toISOString();
        const last24StartDate = new Date(now);
        last24StartDate.setFullYear(last24StartDate.getFullYear() - 2);
        const last24Start = last24StartDate.toISOString();
        const last36StartDate = new Date(now);
        last36StartDate.setFullYear(last36StartDate.getFullYear() - 3);
        const last36Start = last36StartDate.toISOString();

        const baseParams = scope === 'kotilinga'
          ? { ...kotilingaIncludeCategoryParams(), status: 'completed' }
          : { ...templeExcludeCategoryParams(), status: 'completed' };

        const [overallRes, thisYearRes, last12Res, last24Res, last36Res] = await Promise.all([
          get('/ldb/stats/product-summary', baseParams),
          get('/ldb/stats/product-summary', { ...baseParams, start: thisYearStart, end: endIso }),
          get('/ldb/stats/product-summary', { ...baseParams, start: last12Start, end: endIso }),
          get('/ldb/stats/product-summary', { ...baseParams, start: last24Start, end: endIso }),
          get('/ldb/stats/product-summary', { ...baseParams, start: last36Start, end: endIso }),
        ]);

        if (!ignore) {
          setProductSnapshots({
            overall: overallRes?.summary || null,
            thisYear: thisYearRes?.summary || null,
            last12: last12Res?.summary || null,
            last24: last24Res?.summary || null,
            last36: last36Res?.summary || null,
          });
        }
      } catch {
        if (!ignore) setProductSnapshots(null);
      }
    })();
    return () => {
      ignore = true;
    };
  }, []);

  const periodLabel = useMemo(() => {
    if (rangePreset === 'custom' && startDate && endDate) return `${startDate} to ${endDate}`;
    const p = PRESETS.find((x) => x.k === rangePreset);
    return p ? p.t : rangePreset;
  }, [rangePreset, startDate, endDate]);

  const revenueChartData = useMemo(
    () =>
      revenueSeries.map((s) => ({
        ...s,
        label: formatLabelByInterval(s.bucket, chartInterval),
        amountNum: parseFloat(s.amount) || 0,
      })),
    [revenueSeries, chartInterval],
  );

  const orderChartData = useMemo(
    () =>
      orderCountSeries.map((s) => ({
        ...s,
        label: formatLabelByInterval(s.bucket, chartInterval),
        ordersNum: parseInt(s.orders, 10) || 0,
      })),
    [orderCountSeries, chartInterval],
  );

  return (
    <PageLayout contentMaxWidth="lg" showHeaderImage={false} showOrgHeader>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
          {scopeLabel} Dashboard
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Management overview of {scopeLabel}-only donations, pledges, products, and trends.
        </Typography>
      </Box>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'flex-start', md: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel>Period</InputLabel>
            <Select
              label="Period"
              value={rangePreset}
              onChange={(e) => setRangePreset(e.target.value)}
            >
              {PRESETS.map((p) => (
                <MenuItem key={p.k} value={p.k}>
                  {p.t}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {rangePreset === 'custom' && (
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField
                size="small"
                label="Start date"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                size="small"
                label="End date"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Stack>
          )}
          <Box sx={{ flexGrow: 1 }} />
          <Typography variant="body2" color="text.secondary">
            {periodLabel}
          </Typography>
        </Stack>
      </Paper>

      {error && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography color="error" variant="body2">
            {error}
          </Typography>
        </Paper>
      )}

      {loading ? (
        <Typography color="text.secondary">Loading {scopeLabel.toLowerCase()} dashboard…</Typography>
      ) : (
        <>
          {summary && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>Revenue</Typography>
              <Grid container spacing={1.5}>
                <Grid item xs={12} sm={4}>
                  <Card variant="outlined" sx={{ bgcolor: 'grey.50' }}>
                    <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                      <Typography color="text.secondary" variant="caption" display="block">Total revenue</Typography>
                      <Typography variant="h6">{formatCurrency(summary.total_revenue)}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Card variant="outlined" sx={{ bgcolor: 'grey.50' }}>
                    <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                      <Typography color="text.secondary" variant="caption" display="block">Order count</Typography>
                      <Typography variant="h6">{summary.order_count ?? 0}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Card variant="outlined" sx={{ bgcolor: 'grey.50' }}>
                    <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                      <Typography color="text.secondary" variant="caption" display="block">Avg order value</Typography>
                      <Typography variant="h6">{formatCurrency(summary.avg_order_value)}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Box>
          )}

          {(donorSnapshots || productSnapshots) && (
            <Grid container spacing={2} sx={{ mb: 2 }}>
              {donorSnapshots && (
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>Donors</Typography>
                  <Grid container spacing={1.5}>
                    <Grid item xs={6} sm={4}>
                      <Card variant="outlined" sx={{ bgcolor: 'grey.50' }}>
                        <CardContent sx={{ py: 1, '&:last-child': { pb: 1 } }}>
                          <Typography color="text.secondary" variant="caption" display="block">Total (all time)</Typography>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{Number(donorSnapshots.overall?.donors_count || 0).toLocaleString()}</Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={6} sm={4}>
                      <Card variant="outlined" sx={{ bgcolor: 'grey.50' }}>
                        <CardContent sx={{ py: 1, '&:last-child': { pb: 1 } }}>
                          <Typography color="text.secondary" variant="caption" display="block">Active (this year)</Typography>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{Number(donorSnapshots.thisYear?.donors_count || 0).toLocaleString()}</Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={6} sm={4}>
                      <Card variant="outlined" sx={{ bgcolor: 'grey.50' }}>
                        <CardContent sx={{ py: 1, '&:last-child': { pb: 1 } }}>
                          <Typography color="text.secondary" variant="caption" display="block">Active (last 12 mo)</Typography>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{Number(donorSnapshots.last12?.donors_count || 0).toLocaleString()}</Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={6} sm={4}>
                      <Card variant="outlined" sx={{ bgcolor: 'grey.50' }}>
                        <CardContent sx={{ py: 1, '&:last-child': { pb: 1 } }}>
                          <Typography color="text.secondary" variant="caption" display="block">Active (last 2 yrs)</Typography>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{Number(donorSnapshots.last24?.donors_count || 0).toLocaleString()}</Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={6} sm={4}>
                      <Card variant="outlined" sx={{ bgcolor: 'grey.50' }}>
                        <CardContent sx={{ py: 1, '&:last-child': { pb: 1 } }}>
                          <Typography color="text.secondary" variant="caption" display="block">Active (last 3 yrs)</Typography>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{Number(donorSnapshots.last36?.donors_count || 0).toLocaleString()}</Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={6} sm={4}>
                      <Card variant="outlined" sx={{ bgcolor: 'grey.50' }}>
                        <CardContent sx={{ py: 1, '&:last-child': { pb: 1 } }}>
                          <Typography color="text.secondary" variant="caption" display="block">Avg per donor (12 mo)</Typography>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            {(() => {
                              const snap = donorSnapshots.last12;
                              const total = snap ? parseFloat(snap.total_donations || 0) : 0;
                              const donors = Number(snap?.donors_count || 0);
                              if (!snap || !donors || !total) return '--';
                              return formatCurrency(total / donors);
                            })()}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>
                </Grid>
              )}
              {productSnapshots && (
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>Products</Typography>
                  <Grid container spacing={1.5}>
                    <Grid item xs={6} sm={4}>
                      <Card variant="outlined" sx={{ bgcolor: 'grey.50' }}>
                        <CardContent sx={{ py: 1, '&:last-child': { pb: 1 } }}>
                          <Typography color="text.secondary" variant="caption" display="block">Total (all time)</Typography>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{Number(productSnapshots.overall?.product_count || 0).toLocaleString()}</Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={6} sm={4}>
                      <Card variant="outlined" sx={{ bgcolor: 'grey.50' }}>
                        <CardContent sx={{ py: 1, '&:last-child': { pb: 1 } }}>
                          <Typography color="text.secondary" variant="caption" display="block">Active (this year)</Typography>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{Number(productSnapshots.thisYear?.product_count || 0).toLocaleString()}</Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={6} sm={4}>
                      <Card variant="outlined" sx={{ bgcolor: 'grey.50' }}>
                        <CardContent sx={{ py: 1, '&:last-child': { pb: 1 } }}>
                          <Typography color="text.secondary" variant="caption" display="block">Active (last 12 mo)</Typography>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{Number(productSnapshots.last12?.product_count || 0).toLocaleString()}</Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={6} sm={4}>
                      <Card variant="outlined" sx={{ bgcolor: 'grey.50' }}>
                        <CardContent sx={{ py: 1, '&:last-child': { pb: 1 } }}>
                          <Typography color="text.secondary" variant="caption" display="block">Active (last 2 yrs)</Typography>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{Number(productSnapshots.last24?.product_count || 0).toLocaleString()}</Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={6} sm={4}>
                      <Card variant="outlined" sx={{ bgcolor: 'grey.50' }}>
                        <CardContent sx={{ py: 1, '&:last-child': { pb: 1 } }}>
                          <Typography color="text.secondary" variant="caption" display="block">Active (last 3 yrs)</Typography>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{Number(productSnapshots.last36?.product_count || 0).toLocaleString()}</Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={6} sm={4}>
                      <Card variant="outlined" sx={{ bgcolor: 'grey.50' }}>
                        <CardContent sx={{ py: 1, '&:last-child': { pb: 1 } }}>
                          <Typography color="text.secondary" variant="caption" display="block">Avg per product (12 mo)</Typography>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            {(() => {
                              const snap = productSnapshots.last12;
                              const total = snap ? parseFloat(snap.total_revenue || 0) : 0;
                              const count = Number(snap?.product_count || 0);
                              if (!snap || !count || !total) return '--';
                              return formatCurrency(total / count);
                            })()}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>
                </Grid>
              )}
            </Grid>
          )}

          {revenueChartData.length > 0 && (
            <Paper sx={{ p: 2, mb: 2 }}>
              <Typography variant="h6" gutterBottom>
                Revenue over time (Temple only)
              </Typography>
              <ResponsiveContainer width="100%" height={260}>
                <LineChart data={revenueChartData} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="label"
                    tick={{ fontSize: 10 }}
                    interval={
                      revenueChartData.length > 12
                        ? Math.max(0, Math.floor((revenueChartData.length - 1) / 10))
                        : 0
                    }
                  />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `$${v}`} />
                  <Tooltip formatter={(v) => [formatCurrency(v), 'Revenue']} labelFormatter={(l) => l} />
                  <Line
                    type="monotone"
                    dataKey="amountNum"
                    stroke="#1a237e"
                    strokeWidth={2}
                    dot={{ r: 3 }}
                    name="Revenue"
                  />
                </LineChart>
              </ResponsiveContainer>
            </Paper>
          )}

          {orderChartData.length > 0 && (
            <Paper sx={{ p: 2, mb: 2 }}>
              <Typography variant="h6" gutterBottom>
                Order count over time ({scopeLabel} only)
              </Typography>
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={orderChartData} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="label"
                    tick={{ fontSize: 10 }}
                    interval={
                      orderChartData.length > 12
                        ? Math.max(0, Math.floor((orderChartData.length - 1) / 10))
                        : 0
                    }
                  />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Bar dataKey="ordersNum" fill="#2e7d32" name="Orders" />
                </BarChart>
              </ResponsiveContainer>
            </Paper>
          )}

          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2, height: '100%' }}>
                <Typography variant="h6" gutterBottom>
                  Revenue by product ({scopeLabel} only)
                </Typography>
                <Box
                  sx={{
                    maxHeight: 360,
                    overflow: 'auto',
                    '& tbody tr:nth-of-type(even)': { bgcolor: 'grey.100' },
                    '& tbody tr:hover': { bgcolor: 'rgba(25,118,210,0.06)' },
                  }}
                >
                  <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
                    <thead>
                      <tr>
                        <th style={{ textAlign: 'left', padding: '4px 8px', width: '60%' }}>Product</th>
                        <th style={{ textAlign: 'right', padding: '4px 8px', width: '20%' }}>Revenue</th>
                        <th style={{ textAlign: 'right', padding: '4px 8px', width: '20%' }}>Orders</th>
                      </tr>
                    </thead>
                    <tbody>
                      {productSplit.length === 0 ? (
                        <tr>
                          <td colSpan={3} style={{ padding: '8px' }}>
                            <Typography variant="body2" color="text.secondary">
                              No data for selected period.
                            </Typography>
                          </td>
                        </tr>
                      ) : (
                        productSplit
                          .slice(productPage * ROWS_PER_PAGE, productPage * ROWS_PER_PAGE + ROWS_PER_PAGE)
                          .flatMap((r) => {
                            const isExpanded = expandedProductId === r.product_id;
                            const variations = variationsByProductId[r.product_id];
                            const isLoading = isExpanded && variations === undefined && loadingVariations;
                            const isError = isExpanded && variations === null;
                            const rows = [
                              <tr
                                key={r.product_id}
                                onClick={() => {
                                  setExpandedProductId((prev) => (prev === r.product_id ? null : r.product_id));
                                  if (!isExpanded && variationsByProductId[r.product_id] === undefined) fetchProductVariations(r.product_id);
                                }}
                                style={{ cursor: 'pointer' }}
                              >
                                <td style={{ padding: '4px 8px' }}>
                                  {isExpanded ? '▼ ' : '▶ '}
                                  {(r.product_name || `Product ${r.product_id}`).slice(0, 58)}
                                </td>
                                <td style={{ padding: '4px 8px', textAlign: 'right' }}>{formatCurrency(r.amount)}</td>
                                <td style={{ padding: '4px 8px', textAlign: 'right' }}>{r.orders ?? 0}</td>
                              </tr>,
                            ];
                            if (isExpanded) {
                              if (isLoading) {
                                rows.push(<tr key={`${r.product_id}-loading`}><td colSpan={3} style={{ padding: '8px 8px 8px 24px', fontStyle: 'italic' }}>Loading variations…</td></tr>);
                              } else if (isError) {
                                rows.push(<tr key={`${r.product_id}-err`}><td colSpan={3} style={{ padding: '8px 8px 8px 24px', color: 'text.secondary' }}>Failed to load variations.</td></tr>);
                              } else if (Array.isArray(variations) && variations.length === 0) {
                                rows.push(<tr key={`${r.product_id}-empty`}><td colSpan={3} style={{ padding: '8px 8px 8px 24px' }}>No variations.</td></tr>);
                              } else if (Array.isArray(variations)) {
                                variations.forEach((v) => {
                                  rows.push(
                                    <tr key={`${r.product_id}-v-${v.variation_id}`} style={{ bgcolor: 'grey.50' }}>
                                      <td style={{ padding: '4px 8px 4px 24px' }}>{(v.variation_name || '—').slice(0, 56)}</td>
                                      <td style={{ padding: '4px 8px', textAlign: 'right' }}>{formatCurrency(v.amount)}</td>
                                      <td style={{ padding: '4px 8px', textAlign: 'right' }}>{v.orders ?? 0}</td>
                                    </tr>
                                  );
                                });
                              }
                            }
                            return rows;
                          })
                      )}
                    </tbody>
                  </table>
                </Box>
                {productSplit.length > ROWS_PER_PAGE && (
                  <TablePagination
                    component="div"
                    count={productSplit.length}
                    rowsPerPage={ROWS_PER_PAGE}
                    rowsPerPageOptions={[ROWS_PER_PAGE]}
                    page={productPage}
                    onPageChange={(_, p) => setProductPage(p)}
                    labelDisplayedRows={({ from, to, count }) => `${from}-${to} of ${count}`}
                  />
                )}
              </Paper>
            </Grid>

            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2, height: '100%' }}>
                <Typography variant="h6" gutterBottom>
                  Revenue by category ({scopeLabel} only)
                </Typography>
                <Box
                  sx={{
                    maxHeight: 360,
                    overflow: 'auto',
                    '& tbody tr:nth-of-type(even)': { bgcolor: 'grey.100' },
                    '& tbody tr:hover': { bgcolor: 'rgba(25,118,210,0.06)' },
                  }}
                >
                  <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
                    <thead>
                      <tr>
                        <th style={{ textAlign: 'left', padding: '4px 8px', width: '60%' }}>Category</th>
                        <th style={{ textAlign: 'right', padding: '4px 8px', width: '20%' }}>Revenue</th>
                        <th style={{ textAlign: 'right', padding: '4px 8px', width: '20%' }}>Orders</th>
                      </tr>
                    </thead>
                    <tbody>
                      {categorySplit.length === 0 ? (
                        <tr>
                          <td colSpan={3} style={{ padding: '8px' }}>
                            <Typography variant="body2" color="text.secondary">
                              No data for selected period.
                            </Typography>
                          </td>
                        </tr>
                      ) : (
                        categorySplit
                          .slice(categoryPage * ROWS_PER_PAGE, categoryPage * ROWS_PER_PAGE + ROWS_PER_PAGE)
                          .flatMap((r) => {
                            const catId = r.category_id;
                            const rowKey = catId != null ? catId : (r.category_slug || String(r.category_name || ''));
                            const isExpanded = expandedCategoryId === rowKey;
                            const products = productsByCategoryId[rowKey];
                            const isLoading = isExpanded && products === undefined && loadingCategoryProducts;
                            const isError = isExpanded && products === null;
                            const rows = [
                              <tr
                                key={rowKey}
                                onClick={() => {
                                  if (catId == null) return;
                                  setExpandedCategoryId((prev) => (prev === rowKey ? null : rowKey));
                                  if (!isExpanded && productsByCategoryId[rowKey] === undefined) fetchCategoryProducts(catId);
                                }}
                                style={{ cursor: catId != null ? 'pointer' : 'default' }}
                              >
                                <td style={{ padding: '4px 8px' }}>
                                  {catId != null && (isExpanded ? '▼ ' : '▶ ')}
                                  {(r.category_name || r.category_slug || 'Category').slice(0, 58)}
                                </td>
                                <td style={{ padding: '4px 8px', textAlign: 'right' }}>{formatCurrency(r.amount)}</td>
                                <td style={{ padding: '4px 8px', textAlign: 'right' }}>{r.orders ?? 0}</td>
                              </tr>,
                            ];
                            if (isExpanded) {
                              if (isLoading) {
                                rows.push(<tr key={`${rowKey}-loading`}><td colSpan={3} style={{ padding: '8px 8px 8px 24px', fontStyle: 'italic' }}>Loading products…</td></tr>);
                              } else if (isError) {
                                rows.push(<tr key={`${rowKey}-err`}><td colSpan={3} style={{ padding: '8px 8px 8px 24px' }}>Failed to load products.</td></tr>);
                              } else if (Array.isArray(products) && products.length === 0) {
                                rows.push(<tr key={`${rowKey}-empty`}><td colSpan={3} style={{ padding: '8px 8px 8px 24px' }}>No products.</td></tr>);
                              } else if (Array.isArray(products)) {
                                products.forEach((p) => {
                                  rows.push(
                                    <tr key={`${rowKey}-p-${p.product_id}`} style={{ bgcolor: 'grey.50' }}>
                                      <td style={{ padding: '4px 8px 4px 24px' }}>{(p.product_name || `Product ${p.product_id}`).slice(0, 56)}</td>
                                      <td style={{ padding: '4px 8px', textAlign: 'right' }}>{formatCurrency(p.amount)}</td>
                                      <td style={{ padding: '4px 8px', textAlign: 'right' }}>{p.orders ?? 0}</td>
                                    </tr>
                                  );
                                });
                              }
                            }
                            return rows;
                          })
                      )}
                    </tbody>
                  </table>
                </Box>
                {categorySplit.length > ROWS_PER_PAGE && (
                  <TablePagination
                    component="div"
                    count={categorySplit.length}
                    rowsPerPage={ROWS_PER_PAGE}
                    rowsPerPageOptions={[ROWS_PER_PAGE]}
                    page={categoryPage}
                    onPageChange={(_, p) => setCategoryPage(p)}
                    labelDisplayedRows={({ from, to, count }) => `${from}-${to} of ${count}`}
                  />
                )}
              </Paper>
            </Grid>
          </Grid>
        </>
      )}
    </PageLayout>
  );
}

